/** Context & blocks side panel for the notification editor: a searchable tree
 *  of the event-context schema paths (walkSchema over the backend-served
 *  schema), the notification's effective `block.*` entries, and the registered
 *  custom template tags. Every row offers Copy and Insert — Insert targets the
 *  last-focused TemplateCodeEditor via the page's focus registry.
 *
 *  On lg+ it renders as an in-flow sticky side panel (flex sibling of the page
 *  body); below lg as a temporary right Drawer.
 */
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InputIcon from '@mui/icons-material/Input';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type MutableRefObject, type ReactNode } from 'react';

import { getEditorMetadata, getEventSchema } from '@/api/notifications';
import { useSnackbar } from '@/components/Snackbar';
import {
  walkSchema,
  type SchemaPathEntry,
  type TemplateCodeEditorHandle,
} from '@/components/TemplateCodeEditor';

import { ValueActions } from '@/components/ValueActions/ValueActions';

import type { EffectiveBlockRow } from './composition/ResolvedBlocksPreview';
import { blockActions } from './notificationLinks';

const PANEL_WIDTH = 380;

interface Props {
  open: boolean;
  onClose: () => void;
  event: string;
  eventLabel?: string | null | undefined;
  tenantId: number | 'system';
  /** Effective block.* entries (shared with ResolvedBlocksPreview). */
  effectiveBlocks: EffectiveBlockRow[];
  insertRegistry: MutableRefObject<TemplateCodeEditorHandle | null>;
}

export function ContextSchemaDrawer({
  open,
  onClose,
  event,
  eventLabel,
  tenantId,
  effectiveBlocks,
  insertRegistry,
}: Props) {
  const theme = useTheme();
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const snackbar = useSnackbar();
  const [search, setSearch] = useState('');
  const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(new Set());

  const schemaQuery = useQuery({
    queryKey: ['nf-event-schema', event],
    queryFn: ({ signal }) => getEventSchema(event, signal),
    staleTime: 30 * 60_000,
    enabled: open,
  });

  const metadataQuery = useQuery({
    queryKey: ['nf-editor-metadata', String(tenantId)],
    queryFn: () => getEditorMetadata(tenantId),
    staleTime: 5 * 60_000,
    enabled: open,
  });

  const entries = useMemo(
    () => (schemaQuery.data ? walkSchema(schemaQuery.data).filter((e) => e.kind !== 'object') : []),
    [schemaQuery.data],
  );

  const q = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      q
        ? entries.filter(
            (e) =>
              e.path.toLowerCase().includes(q) || (e.description ?? '').toLowerCase().includes(q),
          )
        : entries,
    [entries, q],
  );

  const groups = useMemo(() => {
    const map = new Map<string, SchemaPathEntry[]>();
    for (const entry of filtered) {
      const head = entry.path.split('.')[0] ?? entry.path;
      const list = map.get(head);
      if (list) list.push(entry);
      else map.set(head, [entry]);
    }
    return map;
  }, [filtered]);

  const filteredBlocks = q
    ? effectiveBlocks.filter((b) => !b.loading && b.alias.toLowerCase().includes(q))
    : effectiveBlocks.filter((b) => !b.loading);

  const customTags = metadataQuery.data?.custom_tags ?? [];
  const filteredTags = q ? customTags.filter((t) => t.name.toLowerCase().includes(q)) : customTags;

  const copy = (text: string) => {
    void navigator.clipboard.writeText(text).then(
      () => snackbar.show(`Copied ${text}`, 'success'),
      () => snackbar.show('Copy failed.', 'error'),
    );
  };

  const insert = (text: string) => {
    const handle = insertRegistry.current;
    if (!handle) {
      snackbar.show('Focus a template editor first, then insert.', 'info');
      return;
    }
    handle.insertAtCursor(text);
    handle.focus();
  };

  const toggleGroup = (name: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const entryInsertText = (entry: SchemaPathEntry): string =>
    entry.kind === 'array'
      ? `{% for item in ${entry.path} %}\n\n{% endfor %}`
      : `{{ ${entry.path} }}`;

  const content = (
    <Box sx={{ width: PANEL_WIDTH, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle2">Context & blocks</Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {event}
            {eventLabel ? ` — ${eventLabel}` : ''}
          </Typography>
        </Box>
        <IconButton size="small" aria-label="Close panel" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <TextField
        size="small"
        placeholder="Search variables, blocks, tags…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
        <Typography variant="overline" color="text.secondary">
          Context variables
        </Typography>
        {schemaQuery.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {schemaQuery.isError && (
          <Typography variant="body2" color="error">
            Couldn’t load the event schema.
          </Typography>
        )}
        {!schemaQuery.isLoading && !schemaQuery.isError && groups.size === 0 && (
          <Typography variant="body2" color="text.secondary">
            No matching variables.
          </Typography>
        )}
        <List dense disablePadding>
          {[...groups.entries()].map(([name, groupEntries]) => {
            const expanded = q !== '' || openGroups.has(name);
            return (
              <Box key={name}>
                <ListItemButton dense onClick={() => toggleGroup(name)} sx={{ borderRadius: 1 }}>
                  {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', ml: 0.5, flexGrow: 1 }}>
                    {name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {groupEntries.length}
                  </Typography>
                </ListItemButton>
                <Collapse in={expanded}>
                  {groupEntries.map((entry) => (
                    <HoverRow
                      key={`${entry.kind}-${entry.path}`}
                      onCopy={() => copy(entryInsertText(entry))}
                      onInsert={() => insert(entryInsertText(entry))}
                      primary={
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }} noWrap>
                            {entry.path}
                          </Typography>
                          {entry.kind === 'array' && (
                            <Chip size="small" variant="outlined" label="list" sx={{ height: 16 }} />
                          )}
                        </Stack>
                      }
                      secondary={
                        <>
                          {entry.description && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              {entry.description}
                            </Typography>
                          )}
                          {entry.examples && entry.examples.length > 0 && (
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              component="div"
                              noWrap
                            >
                              e.g. {String(entry.examples[0])}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  ))}
                </Collapse>
              </Box>
            );
          })}
        </List>

        <Typography variant="overline" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
          Blocks
        </Typography>
        {filteredBlocks.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No effective blocks.
          </Typography>
        )}
        {filteredBlocks.map((block) => (
          <HoverRow
            key={block.key}
            onCopy={() => copy(`{{ block.${block.alias} }}`)}
            onInsert={() => insert(`{{ block.${block.alias} }}`)}
            primary={
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }} noWrap>
                  {block.blockId != null ? (
                    <ValueActions
                      actions={blockActions({ id: block.blockId, codename: block.alias })}
                    >
                      block.{block.alias}
                    </ValueActions>
                  ) : (
                    <>block.{block.alias}</>
                  )}
                </Typography>
                {block.shadowed && (
                  <Tooltip title="Shadowed by a later block with the same effective alias.">
                    <WarningAmberIcon color="warning" sx={{ fontSize: 14 }} />
                  </Tooltip>
                )}
              </Stack>
            }
            secondary={
              <Typography variant="caption" color="text.secondary" component="div">
                from: {block.from}
              </Typography>
            }
          />
        ))}

        <Typography variant="overline" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
          Custom tags
        </Typography>
        {metadataQuery.isLoading && (
          <Typography variant="caption" color="text.secondary">
            Loading…
          </Typography>
        )}
        {!metadataQuery.isLoading && filteredTags.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No custom tags.
          </Typography>
        )}
        {filteredTags.map((tag) => (
          <HoverRow
            key={`${tag.library}-${tag.name}`}
            onCopy={() => copy(`{% ${tag.name} %}`)}
            onInsert={() => insert(`{% ${tag.name} %}`)}
            primary={
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }} noWrap>
                {tag.name}
              </Typography>
            }
            secondary={
              <Typography variant="caption" color="text.secondary" component="div">
                {tag.library}
              </Typography>
            }
          />
        ))}
      </Box>
    </Box>
  );

  if (lgUp) {
    if (!open) return null;
    return (
      <Paper
        variant="outlined"
        sx={{
          flexShrink: 0,
          alignSelf: 'flex-start',
          position: 'sticky',
          top: 0,
          maxHeight: 'calc(100vh - 120px)',
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        {content}
      </Paper>
    );
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      {content}
    </Drawer>
  );
}

function HoverRow({
  primary,
  secondary,
  onCopy,
  onInsert,
}: {
  primary: ReactNode;
  secondary?: ReactNode;
  onCopy: () => void;
  onInsert: () => void;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.5,
        pl: 3,
        pr: 0.5,
        py: 0.25,
        borderRadius: 1,
        '&:hover': { bgcolor: 'action.hover' },
        '&:hover .row-actions, &:focus-within .row-actions': { opacity: 1 },
      }}
    >
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {primary}
        {secondary}
      </Box>
      <Stack direction="row" className="row-actions" sx={{ opacity: 0, transition: 'opacity 120ms' }}>
        <Tooltip title="Copy">
          <IconButton size="small" aria-label="Copy" onClick={onCopy}>
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Insert into the focused editor">
          <IconButton size="small" aria-label="Insert" onClick={onInsert}>
            <InputIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}
